// src/shared/clientCombobox.js — полето „Клиент" на фактурата (ПРОМТ-19, т. 2).
//
// Преди тук беше търсачка от public/registry-autofill.js, която се избираше
// само с мишка: пише се „алфа", излиза „АЛФА ТРЕЙД ООД", Enter → нищо, а при
// „Запази" — „Изберете клиент." Сега е combobox() (стрелки, Enter, Esc,
// role="combobox", етикетът през field()) и пази всичко, което правеше
// старото поле:
//   - търсене по име или ЕИК;
//   - 9 или 13 цифри, които ги няма в контактите → Търговският регистър →
//     нов клиент (същите съобщения като преди);
//   - едно-единствено съвпадение се избира само при излизане от полето.
// Изборът се пише в скрития hidden (select или input) — формата и записът
// четат hidden.value както досега.

import { el } from './dom.js';
import { api } from './api.js';
import { toast } from './toast.js';
import { combobox } from './combobox.js';

const digits = (s) => String(s == null ? '' : s).replace(/\D/g, '');
// ЕИК (9) или ЕГН/БУЛСТАТ с клон (13) — и нищо друго в полето освен цифри.
const asEik = (s) => { const t = String(s || '').replace(/\s+/g, ''); return /^\d{9}$|^\d{13}$/.test(t) ? t : ''; };

// opts: contacts (масивът на формата — новият клиент се добавя в него),
//       hidden, companyId, onNew(name) — „+ Нов клиент" с изписаното име.
export function clientCombobox({ contacts, hidden, companyId, onNew }) {
  const status = el('div', { class: 'client-status', role: 'status', 'aria-live': 'polite' });
  const setStatus = (text, kind) => { status.textContent = text || ''; status.className = 'client-status' + (kind ? ' ' + kind : ''); };

  function setHidden(c) {
    const id = c ? String(c.id) : '';
    if (c && hidden.tagName === 'SELECT' && ![...hidden.options].some((o) => o.value === id)) {
      hidden.appendChild(el('option', { value: id, text: c.name || '' }));
    }
    hidden.value = id;
    hidden.dispatchEvent(new Event('change', { bubbles: true }));
  }

  let busy = false;
  async function fromRegistry(eik) {
    if (busy) return;
    const existing = contacts.find((c) => digits(c.eik) === eik);
    if (existing) { cbx.setValue(existing.id); setHidden(existing); setStatus('✓ Съществуващ клиент: ' + existing.name, 'ok'); return; }
    busy = true;
    setStatus('Търсене в Търговския регистър…');
    try {
      let info = null;
      try { info = await api('/registry/' + eik); } catch (e) { info = null; }
      if (!info || !info.name) { setStatus('✗ ЕИК ' + eik + ' не е намерен в регистъра. Проверете номера.', 'err'); return; }
      const created = await api('/contacts', { method: 'POST', body: JSON.stringify({
        company_id: companyId, name: info.name, eik,
        vat_number: info.vat_number || '', mol: info.mol || '', address: info.address || '',
        city: info.city || '', email: info.email || '', phone: info.phone || '', website: info.website || '',
      }) });
      contacts.push(created);
      cbx.setValue(created.id);
      setHidden(created);
      setStatus('✓ Нов клиент създаден автоматично: ' + created.name, 'ok');
      toast('Клиентът „' + created.name + '" е добавен.');
    } catch (e) {
      setStatus('✗ ' + ((e && e.message) || 'Грешка при създаване на клиент.'), 'err');
      toast((e && e.message) || 'Грешка при създаване на клиент.', true);
    } finally {
      busy = false;
    }
  }

  const cbx = combobox({
    items: () => contacts,
    label: (c) => String(c.name || ''),
    sub: (c) => (c.eik ? 'ЕИК ' + c.eik : ''),
    value: hidden.value || null,
    placeholder: 'Име или ЕИК на клиента…',
    autoPickSingle: true,
    footerDefault: true,
    onPick: (c) => { setHidden(c); setStatus(c ? '✓ Избран клиент: ' + c.name : '', c ? 'ok' : ''); },
    // Последният ред: ЕИК → регистърът; иначе „+ Нов клиент" с изписаното име.
    footer: {
      label: (q) => (asEik(q) ? 'Извлечи ЕИК ' + asEik(q) + ' от Търговския регистър' : '+ Нов клиент' + (q ? ' „' + q + '“' : '')),
      onPick: (q) => { if (asEik(q)) fromRegistry(asEik(q)); else if (onNew) onNew(q); },
    },
    // Излизане с ЕИК, който го няма в контактите — както старото поле.
    onBlurUnmatched: (q) => { if (asEik(q)) fromRegistry(asEik(q)); },
  });

  const wrap = el('div', { class: 'client-combobox' }, cbx, status);
  wrap.input = cbx.input;
  wrap.setValue = (id) => { cbx.setValue(id); setStatus(''); };
  return wrap;
}
